import { stripDeliveryMarkers } from './directionPrefill';
import { parseLineMarkers, serializeLineMarkers } from './lineMarkers';

import type {
  DubDirection,
  DubDirectionMechanism,
} from '@src/repositories/DubbingRepository';

/**
 * PLAT-12023 — what one press of a speaker's play button does, decided before
 * anything is fetched. Pure: no React, no IO.
 *
 * The button used to play the voice's stock sample whatever the direction said.
 * Now it plays what the RUN would produce for this speaker: their own words in
 * their voice, with the current direction applied. Where that cannot be done
 * the button says why, and never plays the stock sample in its place — a
 * sample that quietly ignores the slider is the thing this replaces.
 *
 * The one exception is a speaker with no dubbed words yet (a language's first
 * dub): there is nothing to direct, so the stock sample plays as before, and
 * its hint says plainly that it does not follow the direction. Disabling the
 * button there left every speaker unplayable the moment a direction was typed.
 */

/** What the host knows about one speaker's preview. */
export interface DirectionPreview {
  /** The direction the run would send right now, or none. */
  direction: DubDirection | undefined;
  /** This speaker's words in the run's span, cut short; null when unknown. */
  text: string | null;
  /** How this speaker's model can be directed, from the capability read. */
  mechanisms: readonly DubDirectionMechanism[];
  /** The speaker, for the sentence that names them. */
  speakerName: string;
}

export type PreviewPlan =
  /**
   * No words known: the stored sample, as before. With a direction, the hint
   * says the sample does not follow it.
   */
  | { kind: 'sample'; hint: string }
  /** Synthesize these words, with the direction when there is one. */
  | { kind: 'audition'; text: string; direction?: DubDirection; hint: string }
  /** Cannot be previewed honestly; the reason is the button's tooltip. */
  | { kind: 'refused'; reason: string };

/** The mechanism a direction of each kind needs the model to take. */
const MECHANISM_FOR: Record<DubDirection['kind'], DubDirectionMechanism> = {
  'free-form': 'native',
  config: 'settings',
  tags: 'tags',
  'tags-by-line': 'tags',
};

/** What a direction of each kind is called in the button's sentences. */
const DIRECTION_NOUN: Record<DubDirection['kind'], string> = {
  'free-form': 'this direction',
  config: 'these settings',
  tags: 'these markers',
  'tags-by-line': 'these markers',
};

export function planPreview(preview: DirectionPreview): PreviewPlan {
  const { direction, text, mechanisms, speakerName } = preview;
  if (!direction) {
    return text
      ? { kind: 'audition', text, hint: 'Plays these words in this voice.' }
      : { kind: 'sample', hint: 'Plays the voice’s sample.' };
  }
  const noun = DIRECTION_NOUN[direction.kind];
  if (!text) {
    return {
      kind: 'sample',
      hint: `Plays the voice’s sample, which doesn’t follow ${noun} — there are no dubbed words to preview yet.`,
    };
  }
  if (!mechanisms.includes(MECHANISM_FOR[direction.kind])) {
    return {
      kind: 'refused',
      reason: `Can’t preview with ${noun}: ${speakerName}’s voice doesn’t take them.`,
    };
  }
  const hint = `Plays these words with ${noun} applied.`;
  if (direction.kind === 'tags') {
    const fitted = fitMarkupToPreview(text, direction.taggedText);
    return {
      kind: 'audition',
      text: fitted.text,
      direction: { kind: 'tags', taggedText: fitted.taggedText },
      hint,
    };
  }
  return { kind: 'audition', text, direction, hint };
}

/**
 * A marked-up line cut to the words a preview plays.
 *
 * The preview plays the speaker's words cut short (`previewSpanText`: up to
 * the first word boundary after 160 characters), while the marked-up line is
 * the whole line. The server refuses a preview whose marked-up line carries
 * other words than the text it is given, or runs past 1,000 characters. So
 * the marked-up line is cut after the same number of words, keeping the
 * markers that sit among the kept words, and the words sent are read back
 * from that cut line — the two always match. A line the preview did not cut
 * is returned as it was.
 */
export function fitMarkupToPreview(
  text: string,
  taggedText: string,
): { text: string; taggedText: string } {
  const keptWords = text.split(/\s+/).filter(Boolean).length;
  const { words, markers } = parseLineMarkers(taggedText);
  const lineWords = words ? words.split(' ') : [];
  if (keptWords >= lineWords.length) return { text, taggedText };
  const cut = serializeLineMarkers(
    lineWords.slice(0, keptWords).join(' '),
    markers.filter(marker => marker.beforeWord < keptWords),
  );
  return { text: stripDeliveryMarkers(cut), taggedText: cut };
}

/**
 * What the row says after a directed preview came back: nothing when the
 * server applied the direction, and the plain truth when it did not — the
 * server that applies a direction to previews is PLAT-12028, and until it is
 * deployed the words play undirected.
 */
export function undirectedReplyNotice(
  sent: DubDirection | undefined,
  applied: boolean,
): string | null {
  if (!sent || applied) return null;
  return `Played without ${DIRECTION_NOUN[sent.kind]} — the server doesn’t apply them to previews yet.`;
}
